'use client';

import { Table } from '@tanstack/react-table';
import { Download, RefreshCw } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui/use-toast';
import {
  DataTableActionBar,
  DataTableActionBarAction,
  DataTableActionBarSelection,
} from '@/components/data-table/data-table-action-bar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { formatMoney } from '@/lib/money';
import { Payment } from '@tagadapay/crm-entities';

interface PaymentsTableActionBarProps {
  table: Table<Payment.Payment>;
  onExport?: () => void;
  /** Resolves to the ids of the payments that were not refunded. */
  onBulkRefund?: (paymentIds: string[], requestId: string) => Promise<string[]>;
  isRefunding?: boolean;
}

export function PaymentsTableActionBar({
  table,
  onExport,
  onBulkRefund,
  isRefunding = false,
}: PaymentsTableActionBarProps) {
  const { t } = useTranslation('payments');
  const rows = table.getFilteredSelectedRowModel().rows;
  const [showRefundDialog, setShowRefundDialog] = React.useState(false);
  const [refundRequestId, setRefundRequestId] = React.useState<string | null>(null);
  const { toast } = useToast();

  // Check if any selected payments are refundable
  const hasRefundablePayments = React.useMemo(() => {
    return rows.some((row) => row.original.isRefundable);
  }, [rows]);

  // Get refundable payments
  const refundablePayments = React.useMemo(() => {
    return rows.filter((row) => row.original.isRefundable);
  }, [rows]);

  // Calculate total refund amount
  const totalRefundAmount = React.useMemo(() => {
    const total = refundablePayments.reduce((sum, row) => sum + (row.original.amount || 0), 0);
    const currency = refundablePayments[0]?.original.currency || 'USD';
    return { total, currency };
  }, [refundablePayments]);

  // Function to handle refund button click
  const handleRefundClick = React.useCallback(() => {
    if (!onBulkRefund) return;

    if (refundablePayments.length === 0) {
      toast({
        title: t('actionBar.noRefundablePayments'),
        description: t('actionBar.noRefundablePaymentsDescription'),
        variant: 'destructive',
      });
      return;
    }

    setRefundRequestId(crypto.randomUUID());
    setShowRefundDialog(true);
  }, [refundablePayments, onBulkRefund, toast, t]);

  // Function to confirm refund
  const confirmRefund = React.useCallback(async () => {
    if (!onBulkRefund || refundRequestId === null) return;

    const paymentIds = refundablePayments.map((row) => row.original.id);
    setShowRefundDialog(false);
    table.toggleAllRowsSelected(false);

    // The payments that were not refunded come back selected, so the operator sees them and
    // can act on them. Rows are keyed by payment id, so this survives the list refetch. #TAG-3003
    const failedIds = await onBulkRefund(paymentIds, refundRequestId);
    if (failedIds.length > 0) {
      table.setRowSelection(Object.fromEntries(failedIds.map((id) => [id, true])));
    }
  }, [refundablePayments, onBulkRefund, refundRequestId, table]);

  return (
    <>
      <DataTableActionBar table={table} visible={rows.length > 0}>
        <DataTableActionBarSelection table={table} />
        <Separator orientation="vertical" className="hidden data-[orientation=vertical]:h-5 sm:block" />
        <div className="flex items-center gap-1.5">
          {onExport && (
            <DataTableActionBarAction
              tooltip={t('actionBar.exportSelected')}
              onClick={onExport}
            >
              <Download className="mr-2 h-4 w-4" />
              {t('export')}
            </DataTableActionBarAction>
          )}

          {onBulkRefund && hasRefundablePayments && (
            <DataTableActionBarAction
              tooltip={t('actionBar.refundPayments')}
              isPending={isRefunding}
              onClick={handleRefundClick}
              variant="destructive"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('refund')}
            </DataTableActionBarAction>
          )}
        </div>
      </DataTableActionBar>

      <AlertDialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('actionBar.refundTitle', { count: refundablePayments.length })}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  {t('actionBar.refundConfirmation', { count: refundablePayments.length })}
                </p>
                <ul className="list-disc space-y-1 pl-5 text-sm">
                  <li>
                    {t('actionBar.processRefundsTotaling', { amount: formatMoney(totalRefundAmount.total, totalRefundAmount.currency) })}
                  </li>
                  <li>{t('actionBar.returnFunds')}</li>
                  <li>{t('actionBar.updateStatuses')}</li>
                  <li>{t('actionBar.notifyCustomers')}</li>
                </ul>
                <p className="text-destructive mt-3 font-medium">{t('actionCannotBeUndone')}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRefund}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('actionBar.refundTitle', { count: refundablePayments.length })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
